/**
 * Cookie utility for invite identification.
 * Used alongside IndexedDB to identify invited persons on this device.
 * Cookies provide a lightweight, server-accessible way to check if a device
 * has already joined an invite, while IndexedDB stores the full record.
 */

const INVITE_COOKIE_PREFIX = 'd2c_invite_';
const COOKIE_EXPIRY_DAYS = 365; // 1 year

/**
 * Sets a cookie to record that this device has joined an invite.
 * @param {string} inviteCode - The invite code
 * @param {string} conversationId - The conversation ID
 */
export function setInviteCookie(inviteCode, conversationId) {
  const name = `${INVITE_COOKIE_PREFIX}${inviteCode}`;
  const value = encodeURIComponent(conversationId);
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Checks if this device has a cookie for a given invite.
 * @param {string} inviteCode
 * @returns {boolean} True if the cookie exists
 */
export function hasInviteCookie(inviteCode) {
  const name = `${INVITE_COOKIE_PREFIX}${inviteCode}=`;
  const cookies = document.cookie.split(';').map(c => c.trim());
  return cookies.some(c => c.startsWith(name));
}

/**
 * Gets the conversation ID stored in the invite cookie.
 * @param {string} inviteCode
 * @returns {string|null} The conversation ID, or null if not found
 */
export function getInviteCookieValue(inviteCode) {
  const name = `${INVITE_COOKIE_PREFIX}${inviteCode}=`;
  const cookies = document.cookie.split(';').map(c => c.trim());
  for (const cookie of cookies) {
    if (cookie.startsWith(name)) {
      return decodeURIComponent(cookie.substring(name.length));
    }
  }
  return null;
}

/**
 * Removes the invite cookie for a given invite code.
 * @param {string} inviteCode
 */
export function removeInviteCookie(inviteCode) {
  const name = `${INVITE_COOKIE_PREFIX}${inviteCode}`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
}

/**
 * Gets all invite codes that this device has cookies for.
 * @returns {string[]} Array of invite codes
 */
export function getAllInviteCookies() {
  const cookies = document.cookie.split(';').map(c => c.trim());
  const inviteCodes = [];
  for (const cookie of cookies) {
    if (cookie.startsWith(INVITE_COOKIE_PREFIX)) {
      const code = cookie.substring(INVITE_COOKIE_PREFIX.length).split('=')[0];
      inviteCodes.push(code);
    }
  }
  return inviteCodes;
}
